import { useState, type CSSProperties } from "react";
import { Link } from "wouter";
import design from "./figma-design.json";
import DemoForm from "./demo-form";
import "./styles.css";

type DesignText = { text: string; x: number; y: number; size: number; tracking: number; family: string; weight: number; italic: boolean; baseline: number; color: string; heading?: boolean };
type Action = { x: number; y: number; w: number; h: number; r: number; label: DesignText; action: string; nav?: boolean };
type Section = { key: string; title: string; anchor?: string | null; width: number; height: number; art: string; text: DesignText[]; actions: Action[] };

function typeStyle(text: DesignText, width: number, height: number, offsetX = 0, offsetY = 0): CSSProperties {
  const gradient = text.color.startsWith("url(");
  const relativeFontSize = (text.size / width) * 100;
  const maxFontSize = text.size * (1900 / width);
  return {
    left: `${((text.x - offsetX) / width) * 100}%`,
    top: `${((text.y - text.size * text.baseline - offsetY) / height) * 100}%`,
    fontSize: `min(${relativeFontSize}cqw, ${maxFontSize}px)`,
    letterSpacing: `${(text.tracking / width) * 100}cqw`,
    fontFamily: `"${text.family}", Arial, sans-serif`,
    fontWeight: text.weight,
    fontStyle: text.italic ? "italic" : undefined,
    color: gradient ? "transparent" : text.color,
    ...({
      "--wide-font-size": `${relativeFontSize}dvw`,
      "--wide-font-max": `${text.size * (1850 / width)}px`,
    } as CSSProperties),
    ...(gradient ? { backgroundImage: "linear-gradient(110deg, #392261, #7749d7 55%, #735db5)", backgroundClip: "text", WebkitBackgroundClip: "text" } : {}),
  };
}

function Text({ text, section }: { text: DesignText; section: Section }) {
  return <span className="design-text" style={typeStyle(text, section.width, section.height)}>{text.text}</span>;
}

function cappedOffset(value: number, width: number): string {
  return `min(${(value / width) * 100}cqw, ${value * (1900 / width)}px)`;
}

function textTop(text: DesignText): number {
  return text.y - text.size * text.baseline;
}

function TextBlock({ texts, section, heading: Heading }: {
  texts: DesignText[];
  section: Section;
  heading?: "h1" | "h2";
}) {
  const originX = Math.min(...texts.map((text) => text.x));
  const originTop = Math.min(...texts.map(textTop));
  const children = texts.map((text, index) => (
    <span
      key={index}
      className="design-text"
      style={{
        ...typeStyle(text, section.width, section.height),
        left: cappedOffset(text.x - originX, section.width),
        top: cappedOffset(textTop(text) - originTop, section.width),
      }}
    >
      {text.text}
    </span>
  ));
  const style = {
    left: `${(originX / section.width) * 100}%`,
    top: `${(originTop / section.height) * 100}%`,
  };
  return Heading
    ? <Heading className="design-heading" style={style}>{children}</Heading>
    : <span className="design-text-block" style={style}>{children}</span>;
}

function groupRelatedText(texts: DesignText[]): DesignText[][] {
  const groups: DesignText[][] = [];
  const remaining = new Set(texts);
  const related = (a: DesignText, b: DesignText) => {
    const baselineGap = Math.abs(a.y - b.y);
    const sameColumn = Math.abs(a.x - b.x) <= 12
      && baselineGap <= Math.max(a.size, b.size) * 3;
    const pricePair = (/^\$/.test(a.text) && /month/i.test(b.text))
      || (/^\$/.test(b.text) && /month/i.test(a.text));
    return sameColumn || (pricePair && Math.abs(a.x - b.x) <= 220 && baselineGap <= 70);
  };

  for (const seed of texts) {
    if (!remaining.has(seed)) continue;
    const group: DesignText[] = [];
    const queue = [seed];
    remaining.delete(seed);
    while (queue.length) {
      const current = queue.shift()!;
      group.push(current);
      for (const candidate of [...remaining]) {
        if (related(current, candidate)) {
          remaining.delete(candidate);
          queue.push(candidate);
        }
      }
    }
    groups.push(group.sort((a, b) => a.y - b.y || a.x - b.x));
  }
  return groups;
}

function sectionBackground(section: Section): string {
  const fullSizeRect = section.art.match(
    new RegExp(`<rect\\b[^>]*width="${section.width}"[^>]*height="${section.height}"[^>]*>`),
  )?.[0];
  return fullSizeRect?.match(/fill="(#[0-9A-Fa-f]{6})"/)?.[1] ?? "#FAFAF7";
}

export function homeV2ActionHref(mode: string, action: string, basePath: string): string {
  if (action === "login" || action === "account") return "/login";
  if (action === "contact") return "/contact";
  if (action === "home" || action === "top") return basePath;
  return `${basePath}#${mode}-${action}`;
}

export function selectedPlanForAction(action: Action): string {
  if (action.action === "plans") {
    return /\$49/.test(action.label.text) ? "Website" : "Not sure yet";
  }
  return action.action.charAt(0).toUpperCase() + action.action.slice(1);
}

export function isLoginAction(action: string): boolean {
  return action === "login" || action === "account";
}

function CanvasSection({ section, mode, basePath, choose }: {
  section: Section;
  mode: string;
  basePath: string;
  choose: (plan: string) => void;
}) {
  const heading = section.text.filter((text) => text.heading);
  const footerLegalText = section.text.find((text) => /Privacy.*Terms/.test(text.text));
  const footerCopyrightText = section.text.find((text) => /© 2026 PersonaOS/.test(text.text));
  const Heading = section.anchor === "meet-mo" ? "h1" : "h2";
  const bodyText = section.text.filter((text) => !text.heading && !/Privacy.*Terms/.test(text.text));
  const bodyTextGroups = groupRelatedText(bodyText);
  const actionLabel = (action: Action) => (
    <span className="design-text" style={{
      ...typeStyle(action.label, section.width, action.h, action.x, action.y),
      left: `${((action.label.x - action.x) / action.w) * 100}%`,
    }}>{action.label.text}</span>
  );
  return (
    <section className="design-section" style={{
      "--section-bg": sectionBackground(section),
    } as CSSProperties}
      id={section.anchor ? `${mode}-${section.anchor}` : undefined}
      aria-label={heading.length ? undefined : section.title.replace(/^\d+\s*·\s*/, "")}>
      <div className="design-canvas" style={{ aspectRatio: `${section.width} / ${section.height}` }}>
        <div className="design-art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: section.art }} />
        {heading.length > 0 && <TextBlock texts={heading} section={section} heading={Heading} />}
        {bodyTextGroups.map((texts, index) => <TextBlock key={index} texts={texts} section={section} />)}
        {footerLegalText && (
          <span className="design-text legal-links" style={typeStyle(footerLegalText, section.width, section.height)}>
            <a href="/privacy">Privacy</a><span> · </span>
            <a href="/terms">Terms</a>
          </span>
        )}
        {footerLegalText && footerCopyrightText && (
          <Link
            className={`design-text footer-login footer-login-${mode}`}
            href="/login"
            style={{
              ...typeStyle(footerLegalText, section.width, section.height),
              ...(mode === "desktop"
                ? {
                    left: `${(footerCopyrightText.x / section.width) * 100}%`,
                    transform: "translateX(calc(-100% - 24px))",
                  }
                : {
                    left: "50%",
                    transform: "translateX(-50%)",
                  }),
            }}
          >
            Login
          </Link>
        )}
        {section.actions.map((action, index) => {
          const style = {
            left: `${(action.x / section.width) * 100}%`,
            top: `${(action.y / section.height) * 100}%`,
            width: cappedOffset(action.w, section.width),
            height: cappedOffset(action.h, section.width),
            borderRadius: `${action.r}px`,
          };
          const href = homeV2ActionHref(mode, action.action, basePath);
          return action.nav && isLoginAction(action.action) ? (
            <Link key={index} className="design-action design-nav-link" style={style} href={href}>{actionLabel(action)}</Link>
          ) : action.nav ? (
            <a key={index} className="design-action design-nav-link" style={style} href={href}>{actionLabel(action)}</a>
          ) : (
            <button key={index} className="design-action" style={style} type="button"
              onClick={() => choose(selectedPlanForAction(action))}>
              {actionLabel(action)}
            </button>
          );
        })}
      </div>
    </section>
  );
}

const faqs = [
  ["What is Mo, exactly?", "An AI marketing agent for your business. He handles the work in your plan, from your website and SEO to social, growth, and follow-up."],
  ["Do I need to know marketing?", "No. You bring the knowledge of your business: your services, your customers, and your goals. Mo handles the marketing work in your plan, with your direction guiding the important decisions."],
  ["Can I keep my existing website?", "Your existing website is part of the setup conversation. The right approach depends on your current platform and what you want Mo to handle."],
  ["Will the content sound like my business?", "Mo starts with your brand, services, customers, and preferences. That context guides the ideas, visuals, and copy made for your business."],
  ["Is ad spend included?", "Advertising spend is separate from your plan. You set the advertising budget; the Growth plan includes the work of creating and managing your campaigns."],
];

function FAQ({ mode }: { mode: string }) {
  return (
    <section
      className={`design-faq design-faq-${mode}`}
      id={`${mode}-faq`}
      aria-labelledby={`${mode}-faq-title`}
      style={{ "--section-bg": "#FAFAF7" } as CSSProperties}
    >
      <div className="design-faq-canvas">
        <div className="faq-intro"><p>GOOD QUESTIONS{mode === "mobile" ? "." : ""}</p><h2 id={`${mode}-faq-title`}>Before you<br />put Mo to work.</h2></div>
        <div className="faq-questions">{faqs.map(([question, answer], index) => (
          <details key={question} open={index === 0}>
            <summary>{question}<span className="faq-indicator" aria-hidden="true" /></summary>
            <p>{mode === "mobile" && index === 0 ? "An AI marketing agent for your business. He handles the work in your plan, from your website and SEO to social and growth." : answer}</p>
          </details>
        ))}</div>
      </div>
    </section>
  );
}

export default function HomeV2Page({ basePath = "/new-home" }: { basePath?: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div className="persona-landing">
      <a className="mo-skip-link" href="#landing-main">Skip to content</a>
      <main id="landing-main">
        {(["desktop", "mobile"] as const).map((mode) => (
          <div key={mode} className={`design-layout design-layout-${mode}`}>
            {(design[mode] as Section[]).map((section) =>
              section.anchor === "faq"
                ? <FAQ key={section.key} mode={mode} />
                : <CanvasSection key={section.key} section={section} mode={mode} basePath={basePath} choose={setSelected} />,
            )}
          </div>
        ))}
      </main>
      <DemoForm plan={selected} close={() => setSelected(null)} />
    </div>
  );
}